import { Box, CircularProgress, useTheme } from "@mui/material";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import {
  RobotoButton,
  RobotoLogo,
  RobotoTypography,
} from "@/shared/components";
import {
  humanReadableRobotoRegion,
  RobotoRegion,
} from "@/shared/domain/orgs/OrgRecord";
import { isAbortError } from "@/shared/errors";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import { AuthCard } from "./AuthCard";
import styles from "./AuthFlowButton.module.css";
import { DataRegionCard } from "./DataRegionCard";
import { LegalFooter } from "./LegalFooter";

interface ConfigureDataRegionFormProps {
  isVisible: boolean;
  regionChosen: (robotoRegion: RobotoRegion) => void;
  goBackPressed: () => void;
}

export const ConfigureDataRegionForm: React.FC<
  ConfigureDataRegionFormProps
> = ({ isVisible, regionChosen, goBackPressed }) => {
  const theme = useTheme();

  const [availableRegions, setAvailableRegions] = React.useState<
    RobotoRegion[]
  >([]);
  const [selectedRegion, setSelectedRegion] =
    React.useState<RobotoRegion | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  const { orgService } = useDomainServices();

  React.useEffect(() => {
    if (!isVisible) {
      return;
    }

    const controller = new AbortController();

    const fetchRegions = async () => {
      setLoading(true);
      setError(null);

      const regions = await orgService.getAvailableDataRegions({
        abortSignal: controller.signal,
      });
      setAvailableRegions(regions);

      if (regions.length > 0) {
        if (regions.includes(RobotoRegion.UsWest)) {
          setSelectedRegion(RobotoRegion.UsWest);
        } else if (regions.includes(RobotoRegion.UsGovWest)) {
          setSelectedRegion(RobotoRegion.UsGovWest);
        } else {
          setSelectedRegion(regions[0]);
        }
      }
    };

    fetchRegions()
      .catch((err) => {
        if (!isAbortError(err)) {
          setError(err instanceof Error ? err : new Error("An error occurred"));
          ErrorMonitoringService.captureError(err);
        }
      })
      .finally(() => {
        setLoading(false);
      });

    return function abortActiveRequests() {
      controller.abort();
    };
  }, [orgService, isVisible]);

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <RobotoLogo sx={{ maxWidth: "250px" }} />

      <AuthCard>
        <Box>
          <RobotoTypography
            variant="h5"
            textAlign={"center"}
            sx={{ marginBottom: theme.spacing(2) }}
          >
            Data Region
          </RobotoTypography>
          <RobotoTypography variant="body2" textAlign={"center"}>
            Select a region where your data should be stored
          </RobotoTypography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            mt: theme.spacing(3),
            gap: theme.spacing(2),
            width: "100%",
          }}
        >
          {error && (
            <RobotoTypography
              variant={"caption"}
              textAlign={"center"}
              sx={{ color: theme.palette.error.main }}
            >
              {error.message}
            </RobotoTypography>
          )}
          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: theme.spacing(1),
              }}
            >
              <RobotoTypography variant="body2">
                Loading available regions...
              </RobotoTypography>
              <CircularProgress size="1.25rem" />
            </Box>
          ) : (
            availableRegions
              .sort((a, b) => {
                return -a.localeCompare(b);
              })
              .map((dataRegion) => {
                return (
                  <DataRegionCard
                    key={dataRegion}
                    title={humanReadableRobotoRegion(dataRegion)}
                    emoji={dataRegion.startsWith("us") ? "🇺🇸" : "🇪🇺"}
                    selected={selectedRegion === dataRegion}
                    regionSelected={() => {
                      setSelectedRegion(dataRegion);
                    }}
                  />
                );
              })
          )}

          <RobotoTypography variant="body2" textAlign={"center"}>
            You can also connect to your own bucket with a premium account.
            Contact us after sign up to learn more.
          </RobotoTypography>
        </Box>
        <Box
          className={styles.authFlowSmallButton}
          sx={{
            justifyContent: "center",
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            mt: theme.spacing(2),
          }}
        >
          <RobotoButton
            className={styles.authFlowSmallButton}
            data-cy={"continue-button"}
            eventName={"GoToNextStep"}
            eventProperties={{ currentStep: "ConfigureDataRegion" }}
            variant="contained"
            disabled={selectedRegion === null}
            sx={{
              marginBottom: theme.spacing(2),
            }}
            onClick={() => {
              if (selectedRegion) {
                regionChosen(selectedRegion);
              }
            }}
          >
            Continue
          </RobotoButton>

          <RobotoButton
            eventName={"GoToPreviousStep"}
            eventProperties={{ currentStep: "ConfigureDataRegion" }}
            onClick={goBackPressed}
            className={styles.authFlowSmallButton}
          >
            Go back a step
          </RobotoButton>
        </Box>
      </AuthCard>
      <LegalFooter />
    </>
  );
};
